/**
 * Shared header for the onboarding guide boxes (``SectionGuide`` /
 * ``ConnectorsIntro``): brand icon chip + title + collapse chevron. Single
 * source of the box chrome so the two surfaces can't drift (DRY).
 */
import { cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { ChevronDown, type LucideIcon } from "lucide-react";

/** Brand focus ring shared across the onboarding guide surfaces (DRY). */
export const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45";

export interface GuideBoxHeaderProps {
  readonly title: React.ReactNode;
  readonly icon: LucideIcon;
  readonly collapsed: boolean;
  readonly onToggle: () => void;
  /** Inner padding of the clickable header. Carrying the padding HERE (not on
   *  the section) is what lets the hover span the full card width. */
  readonly padding?: string;
}

export function GuideBoxHeader({
  title,
  icon: Icon,
  collapsed,
  onToggle,
  padding = "px-4 py-3",
}: GuideBoxHeaderProps): React.ReactElement {
  const { i18n } = useLingui();
  // The whole header is the disclosure control (accordion pattern: heading
  // wraps a full-width button) so the entire row toggles — not just the chevron.
  return (
    <h2 className="m-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-label={collapsed ? i18n._(msg`Espandi la guida`) : i18n._(msg`Comprimi la guida`)}
        className={cn(
          "flex w-full cursor-pointer items-center justify-between gap-3 text-left transition-colors hover:bg-ando-ink/[0.04]",
          padding,
          focusRing,
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span
            aria-hidden
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ando-orange/15 text-ando-orange"
          >
            <Icon size={15} strokeWidth={1.75} />
          </span>
          <span className="truncate font-semibold text-[14px] text-ink-1">{title}</span>
        </span>
        {/* Down when collapsed → up + active-orange when open. */}
        <ChevronDown
          size={16}
          strokeWidth={1.75}
          aria-hidden
          className={cn(
            "shrink-0 transition-[transform,color] motion-reduce:transition-none",
            collapsed ? "rotate-0 text-ink-3" : "rotate-180 text-ando-orange",
          )}
        />
      </button>
    </h2>
  );
}

GuideBoxHeader.displayName = "GuideBoxHeader";
